import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { HistoryPanel } from '../history/HistoryPanel';
import { ConflictNotice } from '../ui/ConflictNotice';
import { trapTab } from '../ui/focus';
import { RowMenu } from '../ui/RowMenu';
import { RuleEditor } from './RuleEditor';
import { ScopeFunctions } from './ScopeFunctions';
import { describeRule, HARDNESS_LABELS, RULES_KEY, RULES_RESOURCE, type Rule } from './types';
import { DialogLayer } from '../ui/Dialog';
import { leaveGuarded } from '../ui/unsaved';

/** Confirmation before a rule is deleted (409 shown in place). */
function DeleteRuleDialog({ rule, onClose }: { rule: Rule; onClose: () => void }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(`/api/rules/${rule.id}?version=${rule.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: RULES_KEY });
      onClose();
    },
  });
  const error = remove.error instanceof ApiError ? remove.error : null;
  return (
    <div className="dialog-backdrop">
      <div
        className="dialog panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-rule-title"
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
          <DialogLayer onClose={onClose} busy={remove.isPending} />
        <h2 id="delete-rule-title">Usunąć regułę?</h2>
        <p>„{rule.name}” przestanie działać. Pozycje, które dodała do sprzętówek bieżących wydarzeń, znikną (poza poprawionymi ręcznie).</p>
        {error?.status === 409 && (
          <ConflictNotice
            onReload={() => {
              void queryClient.invalidateQueries({ queryKey: RULES_KEY });
              onClose();
            }}
          />
        )}
        {error && error.status !== 409 && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" autoFocus disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? 'Usuwanie…' : 'Usuń regułę'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

const PILL: Record<Rule['hardness'], string> = { required: 'chip accent', suggested: 'chip', reminder: 'chip off', service: 'chip', crew: 'chip' };

/** "Reguły zależności": the list as a table (wygląd A), the editor above it. */
export function RulesPage() {
  const { me } = useMe();
  // `undefined`: editor closed; `null`: adding; an id: editing that rule
  const [editing, setEditing] = useState<string | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<Rule | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  /** Owner 2026-10-30 (review I2): the owner's „Zakres → funkcja”, behind its button (rarely changed). */
  const [scopeFunctions, setScopeFunctions] = useState(false);
  const list = useQuery({ queryKey: [...RULES_KEY, 'list'], queryFn: () => api<{ rules: Rule[]; truncated: boolean }>('/api/rules') });
  if (!me) return null;
  const rules = list.data?.rules ?? [];
  const seeHistory = can(me, 'history.entries', 'view');

  return (
    <section className="page" data-feature-id="rules.edit">
      <div className="page-head">
        <h1>Reguły zależności</h1>
        {can(me, RULES_RESOURCE, 'create') && editing === undefined && (
          <button className="btn primary" type="button" onClick={() => leaveGuarded(() => setEditing(null))}>
            Dodaj regułę
          </button>
        )}
        {me.isOwner && (
          <button className="btn" type="button" aria-label="Zakres → funkcja" aria-expanded={scopeFunctions} onClick={() => setScopeFunctions(!scopeFunctions)}>
            ⚙ Zakres → funkcja
          </button>
        )}
      </div>
      {me.isOwner && scopeFunctions && <ScopeFunctions />}
      <p className="muted">„Jeśli w sprzętówce jest X, to Y”: wymagane reguły dodają sprzęt same, sugerowane proponują, przypomnienia pokazują komunikat.</p>

      {editing !== undefined && <RuleEditor key={editing ?? 'new'} {...(editing !== null ? { ruleId: editing } : {})} onClose={() => setEditing(undefined)} />}

      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać reguł'}</p>}
      {list.data && rules.length === 0 && <p className="muted">Nie ma jeszcze reguł.</p>}
      {list.data?.truncated && <p className="notice">Pokazano pierwszych 500 reguł.</p>}
      {rules.length > 0 && (
        <div className="table-wrap">
          <table className="table tpl-table" aria-label="Reguły zależności">
            <thead>
              <tr>
                <th scope="col">Reguła</th>
                <th scope="col" className="rule-kind-col">
                  Rodzaj
                </th>
                <th scope="col" className="tpl-actions-col">
                  <span className="visually-hidden">Działania</span>
                </th>
              </tr>
            </thead>
            {rules.map((r) => {
              // M-f: a rule of „Zakres → funkcja” is changed only there
              const managed = r.source === 'setting';
              const more = [
                ...(seeHistory ? [{ label: historyId === r.id ? 'Ukryj historię zmian' : 'Historia zmian', onSelect: () => setHistoryId(historyId === r.id ? null : r.id) }] : []),
                ...(can(me, RULES_RESOURCE, 'delete') && !managed ? [{ label: 'Usuń', danger: true, onSelect: () => setDeleting(r) }] : []),
              ];
              return (
                <tbody key={r.id} aria-label={r.name}>
                  <tr className={r.active ? undefined : 'inactive'}>
                    <td>
                      <strong>{r.name}</strong>
                      <div className="muted">{describeRule(r)}</div>
                      {managed && <div className="muted">z ustawienia „Zakres → funkcja”</div>}
                      {/* on a phone the kind goes under the name (the column is hidden) */}
                      <div className="rule-kind-inline">
                        <span className={PILL[r.hardness]}>{HARDNESS_LABELS[r.hardness]}</span>
                        {!r.active && <span className="chip off">wyłączona</span>}
                      </div>
                    </td>
                    <td className="rule-kind-col">
                      <span className={PILL[r.hardness]}>{HARDNESS_LABELS[r.hardness]}</span>
                      {!r.active && <span className="chip off">wyłączona</span>}
                    </td>
                    <td className="tpl-actions">
                      <button className="btn small" type="button" aria-label={`${managed ? 'Pokaż' : 'Edytuj'}: ${r.name}`} onClick={() => leaveGuarded(() => setEditing(r.id))}>
                        {can(me, RULES_RESOURCE, 'edit') && !managed ? 'Edytuj' : 'Pokaż'}
                      </button>
                      <RowMenu label={`Więcej działań: ${r.name}`} actions={more} />
                    </td>
                  </tr>
                  {historyId === r.id && seeHistory && (
                    <tr>
                      <td colSpan={3}>
                        <HistoryPanel entityId={r.id} resource={RULES_RESOURCE} />
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
          </table>
        </div>
      )}
      {deleting && <DeleteRuleDialog rule={deleting} onClose={() => setDeleting(null)} />}
    </section>
  );
}
